import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { acquireTokenSafe } from '../App';
import mermaid from 'mermaid';
import { getApiBaseUrl } from '../utils/baseUrl';
import {
    LayoutDashboard,
    TrendingUp,
    CalendarClock,
    ArrowUpDown,
    Users,
    Megaphone,
    Truck,
    GitBranch,
    Sparkles,
    ArrowRight,
    CheckCircle,
    Clock,
    BarChart3,
    PieChart,
    FileText,
    Activity,
    Timer,
    Building2,
    Shield,
    Database,
    Calendar,
    Loader2,
    Archive,
    Euro,
    Package,
    Link2,
    Wrench,
    Pencil,
    Settings,
    Mail,
    HeartPulse,
    Calculator,
    Home as HomeIcon,
} from 'lucide-react';

// Initialize Mermaid for dark theme
mermaid.initialize({
    startOnLoad: false,
    theme: 'dark',
    securityLevel: 'loose',
    fontFamily: 'var(--font-family)',
    themeVariables: {
        primaryColor: '#3b82f6',
        primaryTextColor: '#f8fafc',
        primaryBorderColor: '#475569',
        lineColor: '#64748b',
        secondaryColor: '#1e293b',
        tertiaryColor: '#0f172a',
        background: '#1e293b',
        mainBkg: '#1e293b',
        nodeBorder: '#3b82f6',
        clusterBkg: '#0f172a',
        clusterBorder: '#334155',
        titleColor: '#f8fafc',
        edgeLabelBackground: '#1e293b'
    }
});

// Changelog data interface
interface ChangelogEntry {
    Id?: number;
    date: string;
    version: string;
    description: string;
}

// Fallback changelog data (used if API fails)
const fallbackChangelogData: ChangelogEntry[] = [
    {
        date: '2026-01-26',
        version: '0.6.0',
        description: 'Ken Telemetry Dark Theme applied. Complete UI overhaul with modern dark theme, collapsible sidebar, and improved accessibility.'
    },
    {
        date: '2025-03-28',
        version: '0.1.0',
        description: 'Initial Beta release. Included fundamental reporting from Senta & MKP: Clients Report, Delivery Report, and Partners Report.'
    }
];

// Map report names to routes for linking. Keys are matched verbatim
// (case-sensitive) against changelog descriptions and turned into in-app links.
// When two keys overlap (e.g. "Client Listing" vs "Client Listing Report"),
// parseDescription sorts by length DESC so the longest match wins.
const reportRouteMap: { [key: string]: string } = {
    // Current names
    'Client Listing': '/clients',
    'Client Tenure Report': '/client-tenure',
    'Utilisation Report': '/enhanced-timekeeping',
    'Acquisition Chargebacks': '/acquisition-chargebacks',
    'Corporation Tax Tracker': '/corporation-tax-tracker',
    'HubSpot Deals Pipeline': '/hubspot-deals-pipeline',
    'Renewals: WIP vs Fees': '/renewals-wip-fees',
    'Draft Time': '/draft-timekeeping',
    'Time Corrections': '/time-corrections',
    'Master Services List': '/master-services',
    'Master Services Editor': '/master-services-editor',
    'Xero Contact Linker': '/xero-contact-linker',
    'Client Linking': '/client-linking',
    'Debt Tracking': '/debt-tracking',
    'Client Health': '/client-health',
    'Client Tier Movements': '/client-tier-movements',
    'Scheduled Jobs': '/scheduled-jobs',
    'Scheduled Emails': '/scheduled-emails',
    'Popular section': '/',
    // Legacy names (kept for older changelog entries)
    'Clients Report': '/clients',
    'Delivery Report': '/delivery',
    'Partners Report': '/partners',
    'Time Reporting': '/timekeeping',
    'Expired Client IDs': '/expired-ids',
    'CRO Date Discrepancies': '/cro-discrepancies',
    'CRO Discrepancies': '/cro-discrepancies',
    'Company review for AML': '/review-companies',
    'Xero Inventory': '/xero-inventory',
    'Service Reports': '/service-reports',
    'Work Activity Breakdown': '/work-activity',
    'CRO Data Dump': '/cro-data-dump',
};

// Mermaid integrations chart definition
const integrationsChart = `
graph TD
    A[Kinore Analytics] -.->|Financial Data| B[Xero]
    A -.->|Marketing & Sales| C[HubSpot]
    A -->|Client & Task Mgmt| D[Senta & MKP]
    A -.->|Marketing| E[Google Analytics]
    A -.->|VoIP Data| G[Nuacom]
    A -->|Timekeeping| H[MinuteDock]
    A -->|AML| I[AML HQ]
    A -->|ROI Company Data| J[CRO]
    A -->|UK Company Data| K[Companies House]
    class B,D,H,I,J current;
    class E,G,C,K planned;
    classDef current fill:#1e3a5f,stroke:#3b82f6,stroke-width:2px,color:#e2e8f0;
    classDef planned fill:#1e293b,stroke:#64748b,stroke-dasharray: 5 5,color:#94a3b8;
`;

// Mermaid Chart Component
const MermaidChart: React.FC<{ chart: string; id: string }> = ({ chart, id }) => {
    const chartRef = useRef<HTMLDivElement>(null);
    const [isRendered, setIsRendered] = useState(false);

    useEffect(() => {
        const renderChart = async () => {
            if (chartRef.current && !isRendered) {
                try {
                    chartRef.current.innerHTML = '';
                    const chartId = `mermaid-${id}-${Date.now()}`;
                    const result = await mermaid.render(chartId, chart);
                    if (chartRef.current) {
                        chartRef.current.innerHTML = result.svg;
                        setIsRendered(true);
                    }
                } catch (error) {
                    console.error('Mermaid rendering failed:', error);
                    if (chartRef.current) {
                        chartRef.current.innerHTML = '<p class="mermaid-error">Chart rendering failed</p>';
                    }
                }
            }
        };

        const timer = setTimeout(renderChart, 100);
        return () => clearTimeout(timer);
    }, [chart, id, isRendered]);

    return <div ref={chartRef} className="mermaid-chart-container" />;
};

// Parse description and link report names. Sorts keys longest-first so that
// when two keys overlap (e.g. "Client Listing" inside "Client Listing Report")
// the longer match is preferred even though JS regex alternation is left-most.
const parseDescription = (description: string): React.ReactNode => {
    const reportNames = Object.keys(reportRouteMap)
        .sort((a, b) => b.length - a.length)
        .map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|');
    const regex = new RegExp(`(${reportNames})`, 'g');

    const parts = description.split(regex);
    return parts.map((part, index) => {
        if (reportRouteMap[part]) {
            return (
                <Link key={index} to={reportRouteMap[part]} className="changelog-link">
                    {part}
                </Link>
            );
        }
        return part;
    });
};

const apiUrl = getApiBaseUrl();

const Home: React.FC = () => {
    const { instance, accounts } = useMsal();
    const [changelogData, setChangelogData] = useState<ChangelogEntry[]>(fallbackChangelogData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [popularPages, setPopularPages] = useState<Array<{ PagePath: string; PageName: string; views: number }>>([]);

    // Fetch updates from API
    useEffect(() => {
        const fetchUpdates = async () => {
            try {
                const response = await fetch(`${apiUrl}/api/updates`);
                if (response.ok) {
                    const data = await response.json();
                    setChangelogData(data);
                } else {
                    console.warn('Failed to fetch updates, using fallback data');
                    setError('Could not load updates from server');
                }
            } catch (err) {
                console.warn('Error fetching updates:', err);
                setError('Could not connect to server');
            } finally {
                setLoading(false);
            }
        };
        
        fetchUpdates();
    }, []);

    // Fetch popular pages (last 90 days) for the Popular section
    useEffect(() => {
        const fetchPopular = async () => {
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) return;
            try {
                const res = await fetch(`${apiUrl}/api/telemetry/popular-pages?days=90&limit=6`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    setPopularPages(await res.json());
                }
            } catch (err) {
                console.warn('Failed to fetch popular pages:', err);
            }
        };
        fetchPopular();
    }, [instance, accounts]);

    // Sort changelog by date (newest first)
    const sortedChangelog = useMemo(() => {
        return [...changelogData].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        );
    }, [changelogData]);

    const quickLinks = [
        { to: '/clients', icon: <LayoutDashboard size={24} />, label: 'Clients', description: 'Client analytics - state distribution, churn analysis, and service popularity' },
        { to: '/delivery', icon: <Truck size={24} />, label: 'Delivery', description: 'Task management and workflow tracking with overdue alerts' },
        { to: '/timekeeping', icon: <Timer size={24} />, label: 'Timekeeping', description: 'MinuteDock integration - time tracking and utilization' },
        { to: '/partners', icon: <Users size={24} />, label: 'Partners', description: 'Partner performance and workload distribution' },
        { to: '/service-reports', icon: <PieChart size={24} />, label: 'Services', description: 'Service analytics and revenue breakdown' }
    ];

    // Per-route presentation (icon + label) for the Popular section.
    // Mirrors the sidebar (App.tsx NavItems) so naming/iconography is consistent.
    // If a path comes back from telemetry that isn't in this map, we fall
    // back to a generic icon and use the telemetry-supplied PageName.
    const routeMeta: Record<string, { icon: React.ReactNode; label: string; description: string }> = {
        '/': { icon: <HomeIcon size={24} />, label: 'Home', description: 'Landing page' },
        '/legacy-reports': { icon: <Archive size={24} />, label: 'Legacy Reports', description: 'Original Senta & MKP-derived reports' },
        '/clients': { icon: <Building2 size={24} />, label: 'Client Listing', description: 'Client analytics, churn and service breakdowns' },
        '/cork-clients': { icon: <Building2 size={24} />, label: 'Cork Clients', description: 'Cork-region client portfolio' },
        '/client-health': { icon: <HeartPulse size={24} />, label: 'Client Health', description: 'Engagement, churn risk and health signals' },
        '/client-tier-movements': { icon: <ArrowUpDown size={24} />, label: 'Client Tier Movements', description: 'Clients whose contracted value has crossed the Gold (€10k) or Partner (€3k) line' },
        '/enhanced-timekeeping': { icon: <BarChart3 size={24} />, label: 'Utilisation Report', description: 'Department-level billable hours and utilisation %' },
        '/acquisition-chargebacks': { icon: <Euro size={24} />, label: 'Acquisition Chargebacks', description: 'Hours and cost charged back to ClearGroup, Martin&Co and OnlyAudit' },
        '/corporation-tax-tracker': { icon: <Calculator size={24} />, label: 'Corporation Tax Tracker', description: 'Monthly counts of companies at each stage of the corporation tax workflow' },
        '/hubspot-deals-pipeline': { icon: <TrendingUp size={24} />, label: 'HubSpot Deals Pipeline', description: 'Live HubSpot deals by stage with ARR and pipeline value' },
        '/renewals-wip-fees': { icon: <CalendarClock size={24} />, label: 'Renewals: WIP vs Fees', description: 'Clients renewing each month — 12-month WIP vs fees invoiced and recovery %' },
        '/draft-timekeeping': { icon: <FileText size={24} />, label: 'Draft Time', description: 'Review and approve unsubmitted time entries' },
        '/employee-rates': { icon: <Euro size={24} />, label: 'Employee Rates', description: 'Billable rates per employee' },
        '/master-services': { icon: <Package size={24} />, label: 'Master Services', description: 'Canonical service catalogue' },
        '/master-services-editor': { icon: <Pencil size={24} />, label: 'Master Services Editor', description: 'Edit the canonical service catalogue' },
        '/client-tenure': { icon: <Clock size={24} />, label: 'Client Tenure', description: 'Tenure brackets, gold/partner clients, acquisitions' },
        '/client-linking': { icon: <Link2 size={24} />, label: 'Client Linking', description: 'Cross-system client matching analysis' },
        '/xero-contact-linker': { icon: <Link2 size={24} />, label: 'Xero Contact Linker', description: 'Link Senta & MKP clients to Xero contacts' },
        '/debt-tracking': { icon: <Euro size={24} />, label: 'Debt Tracking', description: 'Receivables and outstanding balances' },
        '/time-corrections': { icon: <Wrench size={24} />, label: 'Time Corrections', description: 'Correct historical time entries and recompute utilisation' },
        '/review-companies': { icon: <Shield size={24} />, label: 'AML Review Companies', description: 'Company review queue for AML' },
        '/expired-ids': { icon: <Shield size={24} />, label: 'Expired IDs', description: 'AML compliance: clients with expired identification' },
        '/cro-discrepancies': { icon: <Shield size={24} />, label: 'CRO Discrepancies', description: 'Mismatches between Senta & MKP and the Irish company registry' },
        '/cro-data-dump': { icon: <Shield size={24} />, label: 'CRO Data Dump', description: 'Raw CRO snapshot data' },
        '/scheduled-jobs': { icon: <Settings size={24} />, label: 'Scheduled Jobs', description: 'Background job runs and status' },
        '/scheduled-emails': { icon: <Mail size={24} />, label: 'Scheduled Emails', description: 'Scheduled email campaigns' },
        '/permissions': { icon: <Shield size={24} />, label: 'Permissions', description: 'Role-based page access' },
        '/system-report': { icon: <Activity size={24} />, label: 'System Report', description: 'Telemetry, logins and page-view stats' },
    };

    // Build the Popular cards. If telemetry returns rows, use them; otherwise
    // fall back to the static quickLinks list so the section is never empty
    // (e.g. before any page-views are logged in a fresh environment).
    const popularLinks = useMemo(() => {
        if (popularPages.length === 0) return quickLinks;
        return popularPages.map((p) => {
            const meta = routeMeta[p.PagePath];
            return {
                to: p.PagePath,
                icon: meta?.icon ?? <BarChart3 size={24} />,
                label: meta?.label ?? p.PageName ?? p.PagePath,
                description: meta?.description ?? `${p.views.toLocaleString()} view${p.views === 1 ? '' : 's'} in the last 90 days`,
            };
        });
    }, [popularPages]);

    const integrations = [
        { name: 'Xero', status: 'connected', icon: <TrendingUp size={20} />, description: 'Financial data, invoicing, P&L' },
        { name: 'Senta & MKP', status: 'connected', icon: <LayoutDashboard size={20} />, description: 'Client & task management' },
        { name: 'MinuteDock', status: 'connected', icon: <Timer size={20} />, description: 'Time tracking & billing' },
        { name: 'AML HQ', status: 'connected', icon: <Shield size={20} />, description: 'Anti-money laundering compliance' },
        { name: 'CRO', status: 'connected', icon: <Building2 size={20} />, description: 'Irish company registry data' },
        { name: 'Companies House', status: 'planned', icon: <Building2 size={20} />, description: 'UK company registry data' },
        { name: 'HubSpot', status: 'planned', icon: <Megaphone size={20} />, description: 'Marketing & sales CRM' },
        { name: 'Google Analytics', status: 'planned', icon: <BarChart3 size={20} />, description: 'Website analytics' },
        { name: 'Nuacom', status: 'planned', icon: <Activity size={20} />, description: 'VoIP call data' }
    ];

    return (
        <div className="home-container">
            {/* Hero Section */}
            <div className="hero-section">
                <div className="hero-content">
                    <h1>
                        <Sparkles className="hero-icon" size={32} />
                        Kinore Analytics
                    </h1>
                    <p className="hero-subtitle">
                        Internal reporting platform providing data insights across departments
                    </p>
                </div>
            </div>

            {/* Popular Reports (based on last 90 days of telemetry) */}
            <section className="home-section">
                <h2 className="section-title">Popular</h2>
                <div className="quick-links-grid">
                    {popularLinks.map((link) => (
                        <Link key={link.to} to={link.to} className="quick-link-card">
                            <div className="quick-link-icon">{link.icon}</div>
                            <div className="quick-link-content">
                                <h3>{link.label}</h3>
                                <p>{link.description}</p>
                            </div>
                            <ArrowRight size={18} className="quick-link-arrow" />
                        </Link>
                    ))}
                </div>
            </section>

            {/* About Section */}
            <section className="home-section">
                <h2 className="section-title">About Kinore Analytics</h2>
                <div className="about-card">
                    <p>
                        Kinore Analytics is an internal reporting platform that provides data insights across departments. 
                        It includes dashboards, report customisation, and data export capabilities, with role-based access 
                        control for security.
                    </p>
                    <div className="about-features">
                        <div className="about-feature">
                            <BarChart3 size={20} />
                            <span>Dashboards</span>
                        </div>
                        <div className="about-feature">
                            <FileText size={20} />
                            <span>Report Customisation</span>
                        </div>
                        <div className="about-feature">
                            <Database size={20} />
                            <span>Data Export</span>
                        </div>
                        <div className="about-feature">
                            <Users size={20} />
                            <span>Role-Based Access</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Integrations Section with Mermaid Chart */}
            <section className="home-section">
                <h2 className="section-title">
                    <GitBranch size={20} />
                    Integrations
                </h2>
                <div className="integrations-container">
                    <div className="mermaid-wrapper">
                        <MermaidChart chart={integrationsChart} id="integrations" />
                        <p className="mermaid-legend">
                            <span className="legend-item current">
                                <span className="legend-dot current"></span>
                                Current integrations
                            </span>
                            <span className="legend-item planned">
                                <span className="legend-dot planned"></span>
                                Planned integrations
                            </span>
                        </p>
                    </div>
                    <div className="integrations-grid">
                        {integrations.map((integration, index) => (
                            <div key={index} className={`integration-card ${integration.status}`}>
                                <div className="integration-icon">{integration.icon}</div>
                                <div className="integration-info">
                                    <h4>{integration.name}</h4>
                                    <p>{integration.description}</p>
                                </div>
                                <span className={`integration-status ${integration.status}`}>
                                    {integration.status === 'connected' ? (
                                        <><CheckCircle size={14} /> Connected</>
                                    ) : (
                                        <><Clock size={14} /> Planned</>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Recent Updates / Changelog Section */}
            <section className="home-section">
                <h2 className="section-title">
                    <Calendar size={20} />
                    Recent Updates
                    {loading && <Loader2 size={16} className="spin" style={{ marginLeft: '8px' }} />}
                </h2>
                {error && (
                    <p className="update-warning" style={{ color: 'var(--color-warning)', fontSize: '0.85rem', marginBottom: '1rem' }}>
                        {error} - showing cached data
                    </p>
                )}
                <div className="changelog-container">
                    <div className="changelog-table">
                        <div className="changelog-header">
                            <div className="changelog-col date">Date</div>
                            <div className="changelog-col version">Version</div>
                            <div className="changelog-col description">Description</div>
                        </div>
                        {loading ? (
                            <div className="changelog-row" style={{ justifyContent: 'center', padding: '2rem' }}>
                                <Loader2 size={24} className="spin" />
                                <span style={{ marginLeft: '0.5rem' }}>Loading updates...</span>
                            </div>
                        ) : (
                            sortedChangelog.map((entry, index) => (
                                <div key={entry.Id || index} className="changelog-row">
                                    <div className="changelog-col date">
                                        <Calendar size={14} />
                                        {new Date(entry.date).toLocaleDateString('en-GB', { 
                                            day: 'numeric', 
                                            month: 'short', 
                                            year: 'numeric' 
                                        })}
                                    </div>
                                    <div className="changelog-col version">
                                        <span className="version-badge">v{entry.version}</span>
                                    </div>
                                    <div className="changelog-col description">
                                        {parseDescription(entry.description)}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
